import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@environment/environment';
import { firstValueFrom } from 'rxjs';
import { LanguageService } from '../i18n/language.service';
import type { TokenResponse } from './auth.models';
import { TokenStorageService } from './token-storage.service';

/** Réponse de GET api/compte/profil. */
interface ProfilResponse {
  /** Prénom de l'utilisateur identifié. */
  prenom: string | null;
  /** Nom de l'utilisateur identifié. */
  nom: string | null;
  /** Courriel du compte, si renseigné. */
  email: string | null;
  /** Téléphone du compte, si renseigné. */
  telephone: string | null;
  /** Langue du compte sur deux lettres, déjà normalisée par l'API, ou `null`. */
  langue: string | null;
}

/**
 * Authentification contre l'endpoint OpenIddict hébergé par KeepControlWeb.Front
 * (`environment.authBaseUrl`) — jamais contre l'API OnlineLifecycle.
 *
 * @remarks
 * Le refresh token est un cookie HttpOnly émis par le domaine de Front (SameSite=Strict,
 * Path=/connect/token) : il n'est jamais lu ni stocké côté client, il repart automatiquement
 * avec `withCredentials: true` vers l'URL ABSOLUE de Front. Le nom de l'utilisateur s'obtient
 * par l'API (le jeton est chiffré, ses claims sont illisibles côté navigateur).
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly storage = inject(TokenStorageService);
  private readonly language = inject(LanguageService);

  private readonly _accessToken = signal<string | null>(this.storage.readAccessToken());
  private readonly _accessTokenExpiry = signal<Date | null>(this.storage.readExpiry());
  private readonly _displayName = signal<string | null>(null);
  private readonly _email = signal<string | null>(null);
  private readonly _telephone = signal<string | null>(null);

  /** L'access token courant, ou `null` si déconnecté. */
  public readonly accessToken = this._accessToken.asReadonly();

  /** L'expiration de l'access token courant, ou `null`. */
  public readonly accessTokenExpiry = this._accessTokenExpiry.asReadonly();

  /** Vrai si un access token est présent. */
  public readonly isLoggedIn = computed(() => this._accessToken() !== null);

  /** Nom affichable de l'utilisateur connecté, ou `null`. */
  public readonly displayName = this._displayName.asReadonly();

  /**
   * Courriel du compte connecté, ou `null` s'il n'est pas renseigné.
   *
   * @remarks
   * Sert à préremplir le contact d'une remontée d'information. `null` ne signifie pas
   * « déconnecté » : le compte peut n'avoir aucun courriel enregistré.
   */
  public readonly email = this._email.asReadonly();

  /** Téléphone du compte connecté, ou `null` s'il n'est pas renseigné. */
  public readonly telephone = this._telephone.asReadonly();

  /** Initiales de l'utilisateur connecté (ex. « LM »), pour l'avatar de session. */
  public readonly initials = computed(() => {
    const name = this._displayName();
    if (!name) return null;
    return name
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => word[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  });

  /**
   * Recharge l'identité affichable d'une session restaurée depuis le stockage.
   *
   * @remarks
   * Appelée par l'initialiseur d'application, et **jamais depuis le constructeur** : une requête
   * HTTP émise pendant la construction du service repasse par la chaîne d'intercepteurs, qui
   * réclame ce même service — Angular lève alors NG0200 (dépendance circulaire). C'est arrivé le
   * 2026-08-25, quand le chargement du fichier de langue a commencé à faire construire ce service
   * depuis un intercepteur, au démarrage.
   *
   * Sans effet si aucune session n'est stockée.
   */
  public restoreSession(): void {
    if (!this.isLoggedIn()) return;

    void this.loadProfile();
  }

  /**
   * Authentifie via `grant_type=password` contre le serveur d'identité de Front.
   *
   * @throws {HttpErrorResponse} Si les identifiants sont refusés.
   */
  public async login(username: string, password: string): Promise<void> {
    this.applyTokenResponse(await this.postToken({ grant_type: 'password', username, password }));
    await this.loadProfile();
  }

  /** Rafraîchit le jeton via le cookie HttpOnly de refresh (`grant_type=refresh_token`). */
  public async refresh(): Promise<void> {
    this.applyTokenResponse(await this.postToken({ grant_type: 'refresh_token' }));
    if (this._displayName() === null) {
      void this.loadProfile();
    }
  }

  /** Déconnexion locale (purge du jeton) et retour à l'accueil. */
  public logout(): void {
    this._accessToken.set(null);
    this._accessTokenExpiry.set(null);
    this._displayName.set(null);
    this._email.set(null);
    this._telephone.set(null);
    this.storage.clear();
    this.router.navigate(['/']);
  }

  private async loadProfile(): Promise<void> {
    try {
      const profil = await firstValueFrom(
        this.http.get<ProfilResponse>(`${environment.apiBaseUrl}/api/compte/profil`),
      );
      this._displayName.set([profil.prenom, profil.nom].filter(Boolean).join(' ') || null);
      this._email.set(profil.email || null);
      this._telephone.set(profil.telephone || null);

      // La langue du compte s'applique à chaque connexion, mais n'écrase JAMAIS un choix explicite
      // fait dans le sélecteur de la barre haute : l'arbitrage vit dans LanguageService.use()
      void this.language.use(profil.langue);
    } catch {
      // Identité indisponible (API OnlineLifecycle arrêtée, jeton refusé…) : la session
      // reste utilisable, seul l'affichage du nom est dégradé
      this._displayName.set(null);
      this._email.set(null);
      this._telephone.set(null);
    }
  }

  private async postToken(fields: Record<string, string>): Promise<TokenResponse> {
    const body = new HttpParams({
      fromObject: { scope: 'openid email offline_access roles', ...fields },
    });

    return firstValueFrom(
      this.http.post<TokenResponse>(`${environment.authBaseUrl}/connect/token`, body.toString(), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        // Dépose/renvoie le cookie HttpOnly de refresh du domaine de Front
        withCredentials: true,
      }),
    );
  }

  private applyTokenResponse(response: TokenResponse): void {
    const expiry = new Date(Date.now() + response.expires_in * 1000);
    this._accessToken.set(response.access_token);
    this._accessTokenExpiry.set(expiry);
    this.storage.write(response.access_token, expiry);
  }
}
